import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categoryQuickLinks } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isHome = location.pathname === '/';

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 60);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isSolid = scrolled || !isHome;
  const textColor = isSolid ? 'text-foreground-900' : 'text-background-50';
  const textHover = isSolid ? 'hover:text-foreground-950' : 'hover:text-background-100';
  const textSubColor = isSolid ? 'text-foreground-700' : 'text-background-50/90';
  const brandColor = isSolid ? 'text-foreground-900' : 'text-background-50';
  const mobileIconColor = isSolid ? 'text-foreground-800' : 'text-background-50';

  return (
    <nav
      className={`navbar fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        isSolid ? 'scrolled' : 'bg-transparent'
      }`}
    >
      <div className="w-full px-6 md:px-10 flex items-center justify-between h-[72px]">
        {/* Brand */}
        <Link
          to="/"
          className={`text-2xl font-heading font-bold tracking-tight whitespace-nowrap transition-colors duration-300 ${brandColor}`}
        >
          Hipobuy
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          <Link
            to="/"
            className={`text-sm font-medium transition-colors whitespace-nowrap ${textSubColor} ${textHover}`}
          >
            Accueil
          </Link>

          <Link
            to="/guide-achat"
            className={`text-sm font-medium transition-colors whitespace-nowrap ${textSubColor} ${textHover}`}
          >
            Guide d&apos;achat
          </Link>

          {/* Catégories Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className={`flex items-center gap-1.5 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${textSubColor} ${textHover}`}
            >
              Catégories
              <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}>
                <i className="ri-arrow-down-s-line text-sm"></i>
              </span>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-[720px] bg-background-50 border border-background-200/70 rounded-xl shadow-xl py-4 px-5 z-50">
                <p className="px-3 py-1 text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-1">Toutes les catégories</p>
                <div className="grid grid-cols-2 gap-x-8 gap-y-0">
                  {categoryQuickLinks.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setDropdownOpen(false)}
                      className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm text-foreground-700 hover:bg-primary-100/70 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-primary-400 flex-shrink-0"></span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          <Link
            to="/article/hipobuy-spreadsheet-guide-complet"
            className={`text-sm font-medium transition-colors whitespace-nowrap ${textSubColor} ${textHover}`}
          >
            Guide SEO
          </Link>
        </div>

        {/* CTA + Mobile Toggle */}
        <div className="flex items-center gap-4">
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="hidden md:inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg shadow-primary-500/20"
          >
            Buy It Now
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-up-line text-sm"></i>
            </span>
          </a>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer transition-colors duration-300 ${mobileIconColor}`}
            aria-label="Menu mobile"
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 shadow-lg max-h-[80vh] overflow-y-auto">
          <div className="px-6 py-4 flex flex-col gap-1">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="px-3 py-3 text-sm font-medium text-foreground-700 hover:text-foreground-950 rounded-md transition-colors flex items-center gap-2"
            >
              <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                <i className="ri-home-4-line"></i>
              </span>
              Accueil
            </Link>
            <Link
              to="/guide-achat"
              onClick={() => setMobileMenuOpen(false)}
              className="px-3 py-3 text-sm font-medium text-foreground-700 hover:text-foreground-950 rounded-md transition-colors flex items-center gap-2"
            >
              <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                <i className="ri-shopping-cart-2-line"></i>
              </span>
              Guide d&apos;achat
            </Link>
            <Link
              to="/article/hipobuy-spreadsheet-guide-complet"
              onClick={() => setMobileMenuOpen(false)}
              className="px-3 py-3 text-sm font-medium text-foreground-700 hover:text-foreground-950 rounded-md transition-colors flex items-center gap-2"
            >
              <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                <i className="ri-article-line"></i>
              </span>
              Guide SEO
            </Link>

            {/* Mobile Category Section */}
            <div className="border-t border-background-200/70 my-2"></div>
            <button
              onClick={() => setMobileCatOpen(!mobileCatOpen)}
              className="flex items-center justify-between px-3 py-3 text-sm font-semibold text-foreground-800 hover:text-foreground-950 rounded-md transition-colors cursor-pointer"
            >
              <span className="flex items-center gap-2">
                <span className="w-5 h-5 flex items-center justify-center text-accent-500">
                  <i className="ri-grid-line"></i>
                </span>
                Catégories Produits
              </span>
              <span className={`w-5 h-5 flex items-center justify-center transition-transform duration-200 ${mobileCatOpen ? 'rotate-180' : ''}`}>
                <i className="ri-arrow-down-s-line"></i>
              </span>
            </button>

            {mobileCatOpen && (
              <div className="grid grid-cols-2 gap-1 px-3 pb-2">
                {categoryQuickLinks.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMobileMenuOpen(false)}
                    className="px-2 py-2.5 text-sm text-foreground-600 hover:bg-primary-100/60 hover:text-primary-700 rounded-md transition-colors whitespace-nowrap"
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            )}

            <div className="border-t border-background-200/70 my-2"></div>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileMenuOpen(false)}
              className="inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-5 py-3 rounded-full text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer mt-2"
            >
              Buy It Now
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-up-line text-sm"></i>
              </span>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}